import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../hooks/use-auth';
import { useAuthStore } from '../store/auth-store';
import { loginSchema, LoginFormData } from '../schemas/auth.schemas';
import { destinoTrasIngresar } from '../destino-tras-ingresar';
import { useToast } from '@/lib/hooks/use-toast';
import { anotarRecordarSesion } from '@/lib/utils/recordar-sesion';
import { ArrowRight, AtSign, Eye, EyeOff, Loader2, LockKeyhole } from 'lucide-react';
import { CAMPO_DE_INGRESO, ICONO_DE_CAMPO, ROTULO_DE_CAMPO } from './marco-de-ingreso';

export function LoginForm() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const elegirPortal = useAuthStore((s) => s.elegirPortal);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recordar, setRecordar] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    /** ¿Ya pasó la contraseña? Lo usa el `catch` para no acusarla de falsa. */
    let contrasenaAceptada = false;
    try {
      setIsLoading(true);
      await login(data);
      contrasenaAceptada = true;

      const destino = destinoTrasIngresar(useAuthStore.getState().portales);

      /*
        Sin ninguna empresa no hay nada que canjear, y el ingreso ya no abre
        sesión por su cuenta: mandarla a `/dashboard` la haría rebotar contra
        el guardián de rutas de vuelta al ingreso, sin decirle nunca por qué.
        Se lo dice acá, y no se anuncia ninguna bienvenida a una aplicación en
        la que no va a entrar.
      */
      if (destino.tipo === 'sin-empresas') {
        toast({
          title: 'No tienes ninguna empresa asignada',
          description: 'Pide acceso a quien administra la plataforma.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Bienvenido de nuevo',
        variant: 'success',
      });

      if (destino.tipo === 'elegir') {
        anotarRecordarSesion(recordar);
        navigate('/portales');
        return;
      }

      /*
        🔴 El canje TIENE que pasar acá, ANTES de la recarga. El ingreso ya no
        abre sesión, y el `portalToken` que permite canjear vive sólo en
        memoria (no se persiste, a propósito): `window.location.href` lo
        borra. Redirigir sin canjear dejaría la página nueva sin sesión y sin
        token para conseguirla, y el guardián de rutas la devolvería al
        ingreso — con la contraseña correcta recién escrita. Le pasaría a
        TODOS los usuarios actuales, que tienen exactamente un portal.
      */
      await elegirPortal(destino.slug);
      // Antes de recargar: si no pidió que se la recuerde, la sesión muere al
      // cerrar el navegador (ver `recordar-sesion.ts`).
      anotarRecordarSesion(recordar);
      window.location.href = `/${destino.slug}/dashboard`;
    } catch (error: any) {
      /*
        🔴 De qué se le echa la culpa. El canje corre dentro de este mismo
        `try`, así que un canje que falla —red caída, 500, la empresa
        desactivada entre el ingreso y el canje— caía en «Credenciales
        inválidas» y mandaba a la persona a volver a escribir una contraseña
        que estaba bien. `contrasenaAceptada` distingue las dos mitades: antes
        de que `login` vuelva, el problema puede ser la contraseña; después, ya
        no puede serlo.
      */
      const detalle = error.response?.data?.error || error.response?.data?.message;
      toast({
        title: contrasenaAceptada ? 'No se pudo entrar a tu empresa' : 'Error al iniciar sesión',
        description: contrasenaAceptada
          ? (detalle ?? 'Tu contraseña es correcta. Prueba de nuevo en un momento.')
          : (detalle ?? 'Credenciales inválidas'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <h2 className="font-titulo text-[26px] font-semibold tracking-tight text-foreground sm:text-[28px]">
        Ingresar a tu cuenta
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <div className="flex flex-col gap-2.5">
          <Label htmlFor="email" className={ROTULO_DE_CAMPO}>
            Correo
          </Label>
          <div className="relative">
            <AtSign className={ICONO_DE_CAMPO} aria-hidden="true" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="nombre@empresa.com"
              {...register('email')}
              error={errors.email?.message}
              disabled={isLoading}
              className={CAMPO_DE_INGRESO}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="password" className={ROTULO_DE_CAMPO}>
              Contraseña
            </Label>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-prixia-gold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <div className="relative">
            <LockKeyhole className={ICONO_DE_CAMPO} aria-hidden="true" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              {...register('password')}
              error={errors.password?.message}
              disabled={isLoading}
              className={`${CAMPO_DE_INGRESO} pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-6 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Checkbox
            id="recordar"
            checked={recordar}
            onCheckedChange={(v) => setRecordar(v === true)}
            disabled={isLoading}
            className="size-5 rounded-md border-border data-[state=checked]:border-primary"
          />
          <Label
            htmlFor="recordar"
            className="cursor-pointer text-sm font-normal text-foreground/80"
          >
            Recordar sesión en este equipo
          </Label>
        </div>

        <Button
          type="submit"
          variant="degradado"
          className="mt-1 h-[52px] w-full rounded-xl text-base shadow-[0_14px_30px_-12px_rgba(93,33,179,0.55)]"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="size-[18px] animate-spin" aria-hidden="true" />
              Ingresando…
            </>
          ) : (
            <>
              Ingresar a Prixia
              <ArrowRight className="size-[18px]" aria-hidden="true" />
            </>
          )}
        </Button>
      </form>
    </div>
  );
}

export default LoginForm;
